import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CommonConfigurator} from '@spartacus/product-configurator/common';
import {
  Configurator,
  ConfiguratorAttributeCompositionContext,
  ConfiguratorAttributeInputFieldComponent,
  ConfiguratorCommonsService,
  ConfiguratorStorefrontUtilsService,
  ConfiguratorUISettingsConfig,
} from '@spartacus/product-configurator/rulebased';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {DcpUiType, FeatureToggleConfig} from '../../models';
import {FeatureTogglesService, StepInputService, ValidationService} from '../../services';
import {CpsConfiguratorFormValidationService} from '../../services/cps-configurator-form-validation.service';
import {valueProviderTokens} from '../../token/tokens';
import * as ConfigUtils from '../../utils';
import {getAddonGroup, LICENSE_MATERIAL_ATTRIBUTE, matchesInterval, TECHNICAL_GROUP_NAME} from '../../utils';

interface QuantityMetaData {
  min: number;
  max: number;
  interval: number;
  salesMetric: string;
  salesMetricTooltip?: string;
}

@UntilDestroy()
@Component({
  selector: 'dcp-configurator-attribute-quantity',
  templateUrl: './dcp-configurator-attribute-quantity.component.html',
  styleUrls: ['./dcp-configurator-attribute-quantity.component.scss'],
  standalone: false,
})
export class DcpConfiguratorAttributeQuantityComponent
  extends ConfiguratorAttributeInputFieldComponent
  implements OnInit, OnDestroy
{
  private unleashToggle: FeatureToggleConfig = inject(valueProviderTokens.FEATURE_TOGGLES);
  currentGroup?: Configurator.Group;
  quantityMetaData?: QuantityMetaData;
  selectedLicenseMaterial?: string;
  showQuantityMetaInfo$?: Observable<boolean>;
  defaultMetaData = {
    interval: 1,
    min: 1,
    max: 9999999999999,
    salesMetric: '',
  };
  isStepInputDisabled$: Observable<boolean | null> = of(null);
  isStepInputRequired$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  valueState$ = new BehaviorSubject('Standard');
  errorMessage: string = 'configurator.error.noError';

  constructor(
    protected validationService: ValidationService,
    protected override config: ConfiguratorUISettingsConfig,
    protected configCommonsService: ConfiguratorCommonsService,
    protected stepInputService: StepInputService,
    protected configuratorFormValidationService: CpsConfiguratorFormValidationService,
    protected featureTogglesService: FeatureTogglesService,
    configuratorAttributeCompositionContext: ConfiguratorAttributeCompositionContext,
    configuratorStorefrontUtilsService: ConfiguratorStorefrontUtilsService,
  ) {
    super(config, configuratorAttributeCompositionContext, configCommonsService, configuratorStorefrontUtilsService);
  }

  protected readonly DcpUiType = DcpUiType;

  override ngOnInit(): void {
    this.configuratorCommonsService
      .getConfiguration(this.attributeComponentContext.owner)
      .pipe(untilDestroyed(this))
      .subscribe((configuration) => {
        this.currentGroup = this.attributeComponentContext.group;
        this.selectedLicenseMaterial = ConfigUtils.getSelectedGroupAttributeValue(
          configuration,
          TECHNICAL_GROUP_NAME,
          LICENSE_MATERIAL_ATTRIBUTE,
        );
        const instanceGroup = this.determineInstanceGroup(configuration);
        this.loadQuantityMetaData(instanceGroup);
        this.attributeInputForm.setValidators([
          Validators.min(this.quantityMetaData!.min),
          Validators.max(this.quantityMetaData!.max),
          Validators.pattern(/^-?\d+(\d+)*$/),
          matchesInterval(this.quantityMetaData!.interval),
        ]);
        if (this.attribute.required) {
          this.isStepInputRequired$.next(true);
          this.attributeInputForm.addValidators([Validators.required]);
        }
        const notInTheLicenseMaterialGroup = this.currentGroup.name !== this.selectedLicenseMaterial;
        if (this.selectedLicenseMaterial && notInTheLicenseMaterialGroup) {
          this.setValue();

          if (
            this.ownerType === CommonConfigurator.OwnerType.CART_ENTRY &&
            this.attribute.required &&
            this.attribute.incomplete &&
            !this.attributeInputForm.value
          ) {
            this.attributeInputForm.markAsTouched();
          }
        }
        this.configuratorFormValidationService.registerFormControl(this.attributeInputForm);
        this.getFeatureToggle();
      });
    this.validateInput();
    this.isStepInputDisabled$ = this.stepInputService.isStepInputDisabled$;
  }

  protected setValue() {
    if (this.attribute.selectedSingleValue) {
      this.attributeInputForm.setValue(this.attribute.selectedSingleValue, {
        emitEvent: false,
      });
    } else {
      this.attributeInputForm.setValue(this.quantityMetaData?.min);
    }
  }

  private getFeatureToggle() {
    this.showQuantityMetaInfo$ = this.featureTogglesService.isEnabled(this.unleashToggle.cpsQuantityMetaInfo);
  }

  loadQuantityMetaData(instanceGroup?: Configurator.Group) {
    if (instanceGroup) {
      this.quantityMetaData = {
        interval: this.getInterval(instanceGroup),
        min: this.getMin(instanceGroup),
        max: this.getMax(instanceGroup),
        salesMetric: this.getSalesMetric(instanceGroup),
        salesMetricTooltip: this.getSalesMetricDescription(instanceGroup),
      };
    } else {
      this.quantityMetaData = this.defaultMetaData;
    }
  }

  onStepInputChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const value = customEvent.detail.value;
    this.attributeInputForm.setValue(value, {emitEvent: false});
    this.validateInput();
    if (!this.hasError()) {
      this.configuratorCommonsService.updateConfiguration(
        this.ownerKey,
        {
          ...this.attribute,
          userInput: `${value}`,
        },
        Configurator.UpdateType.ATTRIBUTE,
      );
    }
  }

  onValueStateChange(event: Event): void {
    const customEvent = event as CustomEvent<{valueState: string}>;
    customEvent.preventDefault();
  }

  private getMin(instanceGroup: Configurator.Group) {
    const attribute = ConfigUtils.findAttributeInGroup(instanceGroup, ConfigUtils.MINIMUM_QUANTITY_ATTRIBUTE);
    return this.getIntValueFromAttributeOrDefault(attribute, this.defaultMetaData.min);
  }

  private getMax(instanceGroup: Configurator.Group) {
    const attribute = ConfigUtils.findAttributeInGroup(instanceGroup, ConfigUtils.MAXIMUM_QUANTITY_ATTRIBUTE);
    return this.getIntValueFromAttributeOrDefault(attribute, this.defaultMetaData.max);
  }

  protected getSalesMetricDescription(instanceGroup: Configurator.Group) {
    const attribute: Configurator.Attribute | undefined = this.getSalesMetricAttribute(instanceGroup);
    return attribute?.longText;
  }

  private getInterval(instanceGroup: Configurator.Group) {
    const attribute = ConfigUtils.findAttributeInGroup(instanceGroup, ConfigUtils.QUANTITY_INCREMENT_ATTRIBUTE);
    return this.getIntValueFromAttributeOrDefault(attribute, this.defaultMetaData.interval);
  }

  private getIntValueFromAttributeOrDefault(attribute: Configurator.Attribute | undefined, defaultValue: number) {
    const value = Number.parseInt(attribute?.selectedSingleValue ?? '', 10);
    if (isNaN(value)) {
      return defaultValue;
    }
    return value;
  }

  protected getSalesMetric(instanceGroup: Configurator.Group) {
    const attribute = this.getSalesMetricAttribute(instanceGroup);
    return attribute?.userInput ?? this.defaultMetaData.salesMetric;
  }

  protected getSalesMetricAttribute(instanceGroup: Configurator.Group) {
    return ConfigUtils.findAttributeInGroup(instanceGroup, ConfigUtils.LICENSE_SALES_UNIT_OF_MEASURE_ATTRIBUTE);
  }

  determineInstanceGroup(configuration: Configurator.Configuration) {
    if (this.currentGroup?.name?.includes('_GENERAL')) {
      return configuration.flatGroups.find((g) => g.name === this.selectedLicenseMaterial);
    } else if (this.currentGroup?.name?.includes('_ADDONS')) {
      return getAddonGroup(configuration, this.attributeComponentContext.attribute.addonName ?? '');
    }
    return undefined;
  }

  override ngOnDestroy(): void {
    this.configuratorFormValidationService.unregisterFormControl(this.attributeInputForm);
  }

  validateInput(): void {
    this.setValueState();
    this.setErrorMessage();
  }

  setValueState(): void {
    this.valueState$.next(this.hasError() ? 'Error' : 'Standard');
  }

  setErrorMessage(): void {
    if (this.hasError()) {
      const error = Object.keys(this.attributeInputForm.errors ?? {})[0];
      this.errorMessage = `configurator.quantity.${this.fieldNameParser(error)}`;
    } else {
      this.errorMessage = 'configurator.error.noError';
    }
  }

  hasError(): boolean {
    return !!this.attributeInputForm.errors;
  }

  getTranslationValues(): number {
    const translation = this.errorMessage;
    if (translation === 'configurator.quantity.minimum') {
      return this.quantityMetaData?.min ?? 0;
    }
    if (translation === 'configurator.quantity.maximum') {
      return this.quantityMetaData?.max ?? 0;
    }
    if (translation === 'configurator.quantity.step') {
      return this.quantityMetaData?.interval ?? 0;
    }
    return 0;
  }

  fieldNameParser(field: string): string {
    switch (field) {
      case 'min':
        return 'minimum';
      case 'pattern':
        return 'onlyWholeNumbers';
      case 'wrongInterval':
        return 'step';
      case 'max':
        return 'maximum';
      default:
        return field;
    }
  }

  isErrorState(state: string): boolean {
    return state === 'Error';
  }
}
